import { Link, useNavigate, useParams } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { humanErrorMessage } from '@/api/errors';
import { CampaignFormFields } from '@/campaigns/campaign-form';
import { CampaignItemsAddDialog } from '@/campaigns/campaign-items-add-dialog';
import { LifecycleButtons } from '@/campaigns/lifecycle-buttons';
import { LogsPanel } from '@/campaigns/logs-panel';
import { ProgressPanel } from '@/campaigns/progress-panel';
import {
  useCampaign,
  useCampaignItems,
  useDeleteCampaign,
  useDestroyCampaign,
  useUpdateCampaign,
  type CampaignItemRecord,
} from '@/campaigns/queries';
import { RecipientsTable } from '@/campaigns/recipients-table';
import { CampaignUpdateSchema, type CampaignUpdateInput } from '@/campaigns/schemas';
import { useClient } from '@/clients/queries';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';
import { DataTable } from '@/components/data-table';
import { DestroyDialog } from '@/components/destroy-dialog';
import { FormDialog } from '@/components/form-dialog';
import { PageShell } from '@/components/page-shell';
import { StatusDot } from '@/components/status-dot';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useWarehouse } from '@/warehouses/queries';

const formatDate = (iso: string): string => new Date(iso).toLocaleString('ru-RU');

const itemsColumns: ColumnDef<CampaignItemRecord>[] = [
  {
    id: 'marketHashName',
    header: 'Предмет',
    cell: ({ row }) => <span className="font-mono text-xs">{row.original.marketHashName}</span>,
  },
  {
    id: 'appid',
    header: 'App',
    cell: ({ row }) => row.original.appid,
  },
  {
    id: 'qty',
    header: 'Qty',
    cell: ({ row }) => row.original.quantity,
  },
  {
    id: 'status',
    header: 'Статус',
    cell: ({ row }) => <StatusDot domain="campaign-item" state={row.original.status} />,
  },
  {
    id: 'createdAt',
    header: 'Добавлен',
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">{formatDate(row.original.createdAt)}</span>
    ),
  },
];

export const CampaignDetailPage = () => {
  const { campaignId } = useParams({ from: '/_protected/campaigns/$campaignId' });
  const navigate = useNavigate();
  const { data: campaign, isLoading } = useCampaign(campaignId);
  const { data: client } = useClient(campaign?.clientId);
  const { data: warehouse } = useWarehouse(campaign?.warehouseId);
  const { data: items } = useCampaignItems(campaignId);
  const updateCampaign = useUpdateCampaign(campaignId);
  const deleteCampaign = useDeleteCampaign();
  const destroyCampaign = useDestroyCampaign();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [destroyOpen, setDestroyOpen] = useState(false);
  const [addItemsOpen, setAddItemsOpen] = useState(false);

  if (isLoading) return <div className="p-6 text-muted-foreground">Загрузка…</div>;
  if (!campaign) return <div className="p-6 text-muted-foreground">Кампания не найдена.</div>;

  const isDraft = campaign.status === 'draft';
  const isDeleted = Boolean(campaign.deletedAt);

  const handleUpdate = (values: CampaignUpdateInput): void => {
    updateCampaign.mutate(values, {
      onSuccess: () => {
        toast.success('Кампания обновлена');
        setEditOpen(false);
      },
      onError: (err) => toast.error(humanErrorMessage(err)),
    });
  };

  // Phase 8.5-04 UX-01 — tier-1 soft-delete (draft only). Server returns 409
  // non_draft_destroy_required for non-draft → UI auto-opens DestroyDialog so
  // operator can pick the tier-3 path. ADR-014.
  const handleDelete = (): void => {
    deleteCampaign.mutate(campaign.id, {
      onSuccess: () => {
        toast.success('Кампания удалена');
        setConfirmDeleteOpen(false);
        void navigate({ to: '/campaigns' });
      },
      onError: (err) => {
        const msg = humanErrorMessage(err);
        if (msg.includes('non_draft_destroy_required')) {
          setConfirmDeleteOpen(false);
          setDestroyOpen(true);
          toast.message('Кампания не в draft — нужен tier-3 destroy с подтверждением имени.');
          return;
        }
        toast.error(msg);
      },
    });
  };

  const handleDestroy = (): void => {
    destroyCampaign.mutate(
      { id: campaign.id, confirmation: campaign.name },
      {
        onSuccess: () => {
          toast.success('Кампания уничтожена');
          setDestroyOpen(false);
          void navigate({ to: '/campaigns' });
        },
        onError: (err) => toast.error(humanErrorMessage(err)),
      },
    );
  };

  return (
    <div className="space-y-4 p-6">
      <Link
        to="/campaigns"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Кампании
      </Link>

      <PageShell
        title={campaign.name}
        description={`status: ${campaign.status}`}
        actions={
          isDeleted ? (
            <Badge variant="destructive">Удалена</Badge>
          ) : (
            <>
              <LifecycleButtons campaign={campaign} itemsTotal={items?.length ?? 0} />
              <Button
                variant="outline"
                onClick={() => {
                  setEditOpen(true);
                }}
                disabled={!isDraft}
              >
                Редактировать
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setConfirmDeleteOpen(true);
                }}
              >
                Удалить
              </Button>
            </>
          )
        }
      >
        <dl className="grid grid-cols-[200px_1fr] gap-y-3 p-6 text-sm">
          <dt className="text-muted-foreground">Клиент</dt>
          <dd>
            <Link
              to="/clients/$clientId"
              params={{ clientId: campaign.clientId }}
              className="hover:underline"
            >
              {client?.name ?? campaign.clientId}
            </Link>
          </dd>
          <dt className="text-muted-foreground">Склад</dt>
          <dd>
            <Link
              to="/warehouses/$warehouseId"
              params={{ warehouseId: campaign.warehouseId }}
              className="hover:underline"
            >
              {warehouse?.name ?? campaign.warehouseId}
            </Link>
          </dd>
          <dt className="text-muted-foreground">Режим выбора</dt>
          <dd>
            <Badge variant="outline">{campaign.itemSelectionMode}</Badge>
          </dd>
          <dt className="text-muted-foreground">Сообщение</dt>
          <dd className="whitespace-pre-wrap">{campaign.messageText || '—'}</dd>
          <dt className="text-muted-foreground">Создана</dt>
          <dd>{formatDate(campaign.createdAt)}</dd>
          <dt className="text-muted-foreground">Обновлена</dt>
          <dd>{formatDate(campaign.updatedAt)}</dd>
        </dl>
      </PageShell>

      <PageShell title="Прогресс">
        <ProgressPanel campaignId={campaign.id} status={campaign.status} />
      </PageShell>

      <PageShell
        title="Элементы кампании"
        actions={
          isDraft && !isDeleted ? (
            <Button
              onClick={() => {
                setAddItemsOpen(true);
              }}
            >
              Добавить элементы
            </Button>
          ) : null
        }
      >
        <DataTable columns={itemsColumns} data={items ?? []} emptyMessage="Пока нет элементов" />
      </PageShell>

      <PageShell title="Получатели">
        <RecipientsTable campaignId={campaign.id} />
      </PageShell>

      <PageShell title="События">
        <LogsPanel campaignId={campaign.id} status={campaign.status} />
      </PageShell>

      <FormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        title="Редактирование кампании"
        schema={CampaignUpdateSchema}
        defaultValues={{
          name: campaign.name,
          messageText: campaign.messageText,
          itemSelectionMode: campaign.itemSelectionMode,
        }}
        onSubmit={handleUpdate}
        isPending={updateCampaign.isPending}
      >
        <CampaignFormFields mode="edit" />
      </FormDialog>

      <CampaignItemsAddDialog
        open={addItemsOpen}
        onOpenChange={setAddItemsOpen}
        campaignId={campaign.id}
      />

      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Удалить кампанию?"
        description={`Кампания «${campaign.name}» будет soft-deleted (для draft) или вы будете перенаправлены на typed-confirmation для tier-3 destroy (если status != draft).`}
        onConfirm={handleDelete}
        isPending={deleteCampaign.isPending}
      />

      <DestroyDialog
        open={destroyOpen}
        onOpenChange={setDestroyOpen}
        title="Уничтожить кампанию (tier-3, irreversible)?"
        description={`Кампания «${campaign.name}» будет hard-deleted. Связанные recipients/items/trades будут удалены каскадом. Действие необратимо. Введите имя для подтверждения.`}
        resourceLabel="имя кампании"
        expectedConfirmation={campaign.name}
        onConfirm={handleDestroy}
        isPending={destroyCampaign.isPending}
      />
    </div>
  );
};
